import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  VStack,
  Box,
  Text,
  Badge,
  Divider,
  HStack,
  Switch,
  Flex,
  Checkbox,
  Button,
} from "@chakra-ui/react";
import { getFilterDisplayString } from "utils/nameUtils";
import { PropagationItem, AvailableNode } from "containers/analysis-plan/Interface";

interface PropagationModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: PropagationItem | null;
  availableNodes: AvailableNode[];
  shouldOverwrite: boolean;
  onToggleOverwrite: (shouldOverwrite: boolean) => void;
  onToggleNode: (nodeId: string) => void;
  onConfirm: () => void;
  title?: string;
}

const PropagationModal: React.FC<PropagationModalProps> = ({
  isOpen,
  onClose,
  item,
  availableNodes,
  shouldOverwrite,
  onToggleOverwrite,
  onToggleNode,
  onConfirm,
  title,
}) => {
  if (!item) return null;

  const getModalTitle = (): string => {
    if (title) return title;

    switch (item.type) {
      case "filter":
        return "Copy Filter Across Plan";
      case "metrics":
        return "Copy Metrics Across Plan";
      case "benchmark":
        return "Copy Benchmark Across Plan";
      default:
        return "Copy Across Plan";
    }
  };

  const getItemDescription = (): React.ReactNode => {
    switch (item.type) {
      case "filter":
        return (
          <Badge
            colorScheme="purple"
            fontSize="sm"
            bg="purple.700"
            color="white"
          >
            {item.filter?.kg_column_header?.name} ={" "}
            {getFilterDisplayString(item.filter!)}
          </Badge>
        );

      case "metrics":
        return (
          <Flex gap={2} flexWrap="wrap">
            {item.metrics?.map((metric, index) => (
              <Badge
                key={index}
                colorScheme="purple"
                fontSize="sm"
                bg="purple.700"
                color="white"
              >
                {metric.header?.name}
              </Badge>
            ))}
          </Flex>
        );

      case "benchmark":
        return (
          <Badge
            colorScheme="purple"
            fontSize="sm"
            bg="purple.700"
            color="white"
          >
            {item.benchmark?.header?.name}
          </Badge>
        );

      default:
        return null;
    }
  };

  const getOverwriteLabel = (): string => {
    switch (item.type) {
      case "filter":
        return `What if there is already a filter on ${item.filter?.kg_column_header?.name}?`;
      case "metrics":
        return "What if there are already metrics in the target nodes?";
      case "benchmark":
        return "What if there is already a benchmark with the same name?";
      default:
        return "What if there are already items in the target nodes?";
    }
  };

  const getOverwriteStatus = (): string => {
    switch (item.type) {
      case "filter":
        return shouldOverwrite ? "REPLACE EXISTING" : "ADD ALONGSIDE";
      case "metrics":
        return shouldOverwrite
          ? "REPLACE ALL EXISTING"
          : "ADD ALONGSIDE EXISTING";
      case "benchmark":
        return shouldOverwrite ? "REPLACE EXISTING" : "ADD ALONGSIDE";
      default:
        return shouldOverwrite ? "REPLACE EXISTING" : "ADD ALONGSIDE";
    }
  };

  const getCopyToLabel = (): string => {
    switch (item.type) {
      case "filter":
        return "Copy filter to:";
      case "metrics":
        return "Copy metrics to:";
      case "benchmark":
        return "Copy benchmark to:";
      default:
        return "Copy to:";
    }
  };

  const selectedCount = availableNodes?.filter((node) => node.selected).length;
  const totalCount = availableNodes.length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{getModalTitle()}</ModalHeader>
        <ModalBody>
          <VStack spacing={2} align="stretch">
            {/* Item Description */}
            <Box>{getItemDescription()}</Box>

            <Divider />

            {/* Overwrite Toggle */}
            <Box>
              <HStack justify="space-between" alignItems="center">
                <VStack align="start" spacing={1}>
                  <Text fontWeight="medium" fontSize="sm" mb={0}>
                    {getOverwriteLabel()}
                  </Text>
                  <Text fontSize="xs" color="gray.600" mb={0}>
                    {getOverwriteStatus()}
                  </Text>
                </VStack>
                <Switch
                  isChecked={shouldOverwrite}
                  onChange={(e) => onToggleOverwrite(e.target.checked)}
                  colorScheme="purple"
                  size="md"
                />
              </HStack>
            </Box>

            <Divider />

            {/* Node Selection */}
            <Box>
              <HStack justify="flex-start" mb={2}>
                <Text fontWeight="medium" mb={1}>
                  {getCopyToLabel()}
                </Text>
                <Text fontWeight="400" mb={1} fontSize="sm">
                  (selected {selectedCount} of {totalCount})
                </Text>
              </HStack>

              <VStack spacing={1} maxH="300px" overflowY="auto" align="stretch">
                {/* Select All option */}
                <Flex
                  p={2}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor={
                    availableNodes.every((d) => d.selected) &&
                    availableNodes.length > 0
                      ? "purple.200"
                      : "gray.200"
                  }
                  bg={
                    availableNodes.every((d) => d.selected) &&
                    availableNodes.length > 0
                      ? "purple.50"
                      : "white"
                  }
                  align="center"
                  gap={2}
                >
                  <Checkbox
                    isChecked={
                      availableNodes.length > 0 &&
                      availableNodes.every((d) => d.selected)
                    }
                    isIndeterminate={
                      availableNodes.some((d) => d.selected) &&
                      !availableNodes.every((d) => d.selected)
                    }
                    onChange={(e) => {
                      const checked = e.target.checked;
                      availableNodes.forEach((node) => {
                        if (node.selected !== checked) {
                          onToggleNode(node.id);
                        }
                      });
                    }}
                    colorScheme="purple"
                    m={0}
                    p={0}
                  />
                  <Text fontSize="sm" fontWeight="bold" m={0} p={0}>
                    Select All
                  </Text>
                </Flex>

                {/* Individual node options */}
                {availableNodes?.map((node) => (
                  <Flex
                    key={node.id}
                    p={2}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor={node.selected ? "purple.200" : "gray.200"}
                    bg={node.selected ? "purple.50" : "white"}
                    align="center"
                    gap={2}
                  >
                    <Checkbox
                      isChecked={node.selected}
                      onChange={() => onToggleNode(node.id)}
                      colorScheme="purple"
                      m={0}
                      p={0}
                    />
                    <Text fontSize="sm" m={0} p={0} fontWeight="600">
                      {node.name}
                    </Text>
                  </Flex>
                ))}
              </VStack>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="purple"
            bg="purple.700"
            onClick={onConfirm}
            isDisabled={selectedCount === 0}
          >
            Propagate to Selected ({selectedCount})
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PropagationModal;
